import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChecklistService from '../../app/services/hooks/useChecklistService';
import AvailableChecklistDataTable from './components/tables/AvailableChecklistDataTable';
import MyResponsesDataTable from './components/tables/MyResponsesDataTable';
import '../../styles/page-components.css';

interface Checklist {
    id: number;
    name: string;
    description: string;
    total_questions: number;
    max_possible_score: number;
}

interface ChecklistResponse {
    id: number;
    checklist_name: string;
    percentage: number;
    scoring_level: {
        label: string;
        color: string;
    };
    status: string;
    completed_at: string;
}

interface ChecklistFilters {
    page: number;
    per_page: number;
    sort: string;
    order: string;
    search: string;
}

interface ResponseFilters {
    page: number;
    per_page: number;
    sort: string;
    order: string;
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Checklists', isCurrent: true }
];

const Checklists = () => {
    const history = useHistory();
    const { 
        getAvailableChecklists, 
        getMyResponses,
        fetchingAvailableChecklists,
        fetchingMyResponses
    } = useChecklistService();
    
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [totalChecklists, setTotalChecklists] = useState(0);
    const [myResponses, setMyResponses] = useState<ChecklistResponse[]>([]);
    const [totalResponses, setTotalResponses] = useState(0);
    const [activeTab, setActiveTab] = useState<'available' | 'my-responses'>('available');

    const [checklistFilter, setChecklistFilter] = useState<ChecklistFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        search: ''
    });

    const [responseFilter, setResponseFilter] = useState<ResponseFilters>({
        page: 1,
        per_page: 10,
        sort: 'completed_at',
        order: 'desc'
    });

    useEffect(() => {
        loadChecklists();
    }, [checklistFilter.page, checklistFilter.per_page, checklistFilter.sort, checklistFilter.order]);

    useEffect(() => {
        loadMyResponses();
    }, [responseFilter.page, responseFilter.per_page, responseFilter.sort, responseFilter.order]);

    const loadChecklists = () => {
        getAvailableChecklists(checklistFilter, {
            onSuccess: (response: ServiceResponse) => {
                setChecklists(response.data.checklists || []);
                setTotalChecklists(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const loadMyResponses = () => {
        getMyResponses(responseFilter, {
            onSuccess: (response: ServiceResponse) => {
                setMyResponses(response.data.responses || []);
                setTotalResponses(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                console.error(response.message);
            }
        });
    };

    const handleStartChecklist = (id: number) => {
        history.push(`/intranet/checklists/${id}/respond`);
    };

    const handleViewResult = (responseId: number) => {
        history.push(`/intranet/checklists/result/${responseId}`);
    };

    return (
        <>
            <div className="container-fluid">
                <div className="page-container">
                    <PageHeader title="" breadcrumbs={breadcrumbs} />

                    <DefaultCard>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="page-title">Checklists</h1>
                                <p className="page-description">
                                    Completa checklists para evaluar tu situación y recibir recomendaciones personalizadas.
                                </p>
                            </div>
                        </div>

                        {/* Tabs */}
                        <ul className="nav nav-tabs mb-4">
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'available' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('available')}
                                >
                                    <i className="fas fa-clipboard-list me-2"></i>
                                    Disponibles
                                </button>
                            </li>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link ${activeTab === 'my-responses' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('my-responses')}
                                >
                                    <i className="fas fa-history me-2"></i>
                                    Mis Resultados ({totalResponses})
                                </button>
                            </li>
                        </ul>

                        {/* Tab: Checklists Disponibles */}
                        {activeTab === 'available' && (
                            <AvailableChecklistDataTable
                                checklists={checklists}
                                totalRows={totalChecklists}
                                loading={fetchingAvailableChecklists}
                                start={handleStartChecklist}
                                filter={checklistFilter}
                                setFilter={setChecklistFilter}
                                onSearch={loadChecklists}
                            />
                        )}

                        {/* Tab: Mis Resultados */}
                        {activeTab === 'my-responses' && (
                            <>
                                {myResponses.length === 0 && !fetchingMyResponses ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fas fa-chart-pie fa-4x mb-3"></i>
                                        <h5>No has completado ningún checklist</h5>
                                        <p>Completa tu primer checklist para ver tus resultados aquí.</p>
                                        <ButtonGeneral
                                            onClick={() => setActiveTab('available')}
                                            title="Ver Checklists Disponibles"
                                            variant="primary"
                                            size="small"
                                        />
                                    </div>
                                ) : (
                                    <MyResponsesDataTable
                                        responses={myResponses}
                                        totalRows={totalResponses}
                                        loading={fetchingMyResponses}
                                        viewResult={handleViewResult}
                                        filter={responseFilter}
                                        setFilter={setResponseFilter}
                                    />
                                )}
                            </>
                        )}
                    </DefaultCard>
                </div>
            </div>
        </>
    );
};

export default Checklists;
